// ─── GBCOSPACK QUOTATION DOCUMENT — DESIGN MOCK DATA ───
// UI preview only. NOT a pricing authority. Do NOT use these values as real quotes.

import type { QuotationDocumentData, MoneyPair } from '@/pages/quotation-design/types';

const seller = {
  companyName: 'GBCOSPACK',
  tagline: 'Global Cosmetic Packaging',
  address: '12F, Gangnam Finance Center, 152 Teheran-ro, Gangnam-gu, Seoul',
  country: 'Republic of Korea',
  contact: '+82-2-1234-5678',
  email: 'trade@gbcospack.com',
  website: 'www.gbcospack.com',
};

const buyer = {
  companyName: 'LUMIÈRE Cosmetics GmbH',
  contactPerson: 'Sarah Müller',
  address: 'Industriestraße 48, 60314 Frankfurt am Main',
  country: 'Germany',
  email: 's.mueller@lumiere-cosmetics.de',
  phone: '+49-69-1234-5678',
};

const EUR = 'EUR';

export const preliminaryQuoteData: QuotationDocumentData = {
  docType: 'preliminary',
  title: 'PRELIMINARY QUOTATION',
  documentNo: 'PQ-2026-000123',
  documentNoLabel: 'Document No.',
  issueDate: '2026-08-24',
  validUntil: 'To be confirmed',
  seller,
  buyer,
  summary: [
    { label: 'Quote Type', value: 'Production' },
    { label: 'Currency', value: 'EUR' },
    { label: 'Customer Reference', value: 'RFQ-2026-0881' },
    { label: 'Trade Terms / Incoterms', value: 'FOB Shanghai Port, China — Incoterms® 2020' },
    { label: 'Delivery / Lead Time', value: 'To be confirmed by GBCOSPACK', pending: true },
    { label: 'Payment Terms', value: 'Subject to final confirmation', pending: true },
    { label: 'Valid Until', value: 'To be confirmed', pending: true },
  ],
  products: [
    {
      name: 'Airless Bottle 30 ml',
      sku: 'GB12345678',
      material: 'PETG / PP',
      capacity: '30 ml',
      size: 'Ø 30 × 95 mm',
      quantity: 5000,
      unitPrice: { usd: '0.670000', selected: '0.58', currency: EUR, unitPrice: true } as MoneyPair,
      amount: { usd: '3350.00', selected: '2898.40', currency: EUR } as MoneyPair,
      imageUrl:
        'https://readdy.ai/api/search-image?query=Elegant%20airless%20cosmetic%20bottle%20with%20pump%2C%2030%20ml%20size%2C%20clear%20transparent%20PETG%20body%20with%20white%20PP%20pump%20actuator%2C%20minimal%20studio%20product%20photography%20on%20a%20clean%20soft%20white%20background%2C%20soft%20diffused%20lighting%2C%20subtle%20reflection%2C%20high-end%20skincare%20packaging%2C%20centered%20composition%2C%20commercial%20catalog%20style&width=240&height=240&seq=quote-airless-30&orientation=squarish',
      finishings: [
        {
          method: 'Silk Screen Printing',
          colorLabel: 'Pantone',
          colorValue: 'PANTONE 186 C',
          quantity: 5000,
          unitPrice: { usd: '0.070000', selected: '0.06', currency: EUR, unitPrice: true } as MoneyPair,
          amount: { usd: '350.00', selected: '302.40', currency: EUR } as MoneyPair,
        },
        {
          method: 'Hot Stamping',
          colorLabel: 'Color',
          colorValue: 'Gold',
          quantity: 5000,
          unitPrice: { usd: '0.110000', selected: '0.10', currency: EUR, unitPrice: true } as MoneyPair,
          amount: { usd: '550.00', selected: '476.10', currency: EUR } as MoneyPair,
        },
      ],
    },
    {
      name: 'Airless Pump Bottle 50 ml',
      sku: 'GB23456789',
      material: 'PP / AS',
      capacity: '50 ml',
      quantity: 3000,
      unitPrice: { usd: '0.820000', selected: '0.71', currency: EUR, unitPrice: true } as MoneyPair,
      amount: { usd: '2460.00', selected: '2128.40', currency: EUR } as MoneyPair,
      imageUrl:
        'https://readdy.ai/api/search-image?query=Modern%20airless%20cosmetic%20bottle%20with%20pump%20dispenser%2C%2050%20ml%20size%2C%20frosted%20white%20body%20with%20silver%20aluminum%20collar%2C%20minimal%20studio%20product%20photography%20on%20clean%20neutral%20light%20gray%20background%2C%20soft%20diffused%20lighting%2C%20gentle%20shadow%2C%20luxury%20skincare%20packaging%2C%20centered%20composition%2C%20professional%20catalog%20style&width=240&height=240&seq=quote-airless-50&orientation=squarish',
      finishings: [
        {
          method: 'Silk Screen Printing',
          colorLabel: 'Pantone',
          colorValue: 'PANTONE Black C',
          quantity: 3000,
          unitPrice: { usd: '0.080000', selected: '0.07', currency: EUR, unitPrice: true } as MoneyPair,
          amount: { usd: '240.00', selected: '207.60', currency: EUR } as MoneyPair,
        },
      ],
    },
  ],
  priceRows: [
    { label: 'Product Subtotal', value: { usd: '5810.00', selected: '5026.15', currency: EUR } as MoneyPair },
    { label: 'Finishing Subtotal', value: { usd: '1140.00', selected: '986.10', currency: EUR } as MoneyPair },
    {
      label: 'PRELIMINARY TOTAL',
      value: { usd: '6950.00', selected: '6011.75', currency: EUR } as MoneyPair,
      strong: true,
      total: true,
    },
  ],
  priceNote:
    'Freight, insurance, taxes and customs duties are excluded unless otherwise stated and are subject to final confirmation.',
  specialRequirements:
    'All items must be food-grade and compliant with EU cosmetic packaging regulations. Provide pre-production sample for approval before mass production.',
  notes: [
    'This preliminary quotation is based on the selected product, quantity and finishing specifications.',
    'Freight, tax, customs duties, delivery terms and payment conditions are subject to final confirmation unless explicitly stated.',
  ],
  statusBadge: { label: '1차 견적 완료', tone: 'success' },
};

export const finalOfferData: QuotationDocumentData = {
  docType: 'final',
  title: 'FINAL OFFER',
  documentNo: 'FO-2026-000123',
  documentNoLabel: 'Offer Number',
  reference: 'PQ-2026-000123',
  referenceLabel: 'Related Preliminary Quote',
  issueDate: '2026-08-24',
  validUntil: '2026-09-08 (Valid for 15 days)',
  seller,
  buyer,
  summary: [
    { label: 'Quote Type', value: 'Production' },
    { label: 'Currency', value: 'EUR' },
    { label: 'Customer Reference', value: 'RFQ-2026-0881' },
    { label: 'Trade Terms / Incoterms', value: 'FOB Shanghai Port, China — Incoterms® 2020' },
    { label: 'Delivery / Lead Time', value: '25–30 days after final artwork approval' },
    { label: 'Payment Terms', value: '30% Deposit / 70% Before Shipment' },
    { label: 'Valid Until', value: '2026-09-08' },
  ],
  products: preliminaryQuoteData.products,
  priceRows: [
    {
      label: 'Product / Finishing Amount',
      value: { usd: '6950.00', selected: '6011.75', currency: EUR } as MoneyPair,
    },
    { label: 'Freight', value: { usd: '480.00', selected: '415.20', currency: EUR } as MoneyPair },
    { label: 'Insurance', value: 'Not included' },
    { label: 'Tax', value: 'Not included' },
    { label: 'Duty', value: 'Not included' },
    {
      label: 'FINAL TOTAL',
      value: { usd: '7430.00', selected: '6426.95', currency: EUR } as MoneyPair,
      strong: true,
      total: true,
    },
  ],
  specialRequirements:
    'All items must be food-grade and compliant with EU cosmetic packaging regulations. Provide pre-production sample for approval before mass production.',
  notes: [
    'This Final Offer supersedes the related Preliminary Quotation (PQ-2026-000123) for confirmed commercial terms.',
  ],
  commercialTerms: [
    { label: 'Payment Terms', value: '30% Deposit / 70% Before Shipment' },
    { label: 'Lead Time', value: '25–30 days after final artwork approval' },
    { label: 'Delivery Terms', value: 'Packed in standard export cartons' },
    { label: 'Trade Terms / Incoterms', value: 'FOB Shanghai Port, China — Incoterms® 2020' },
    { label: 'Named Place', value: 'Shanghai Port, China' },
    { label: 'Valid Until', value: '2026-09-08 (Valid for 15 days)' },
    { label: 'Commercial Notes', value: 'Prices are valid for the specified quantity and specifications only.' },
  ],
  authorization: {
    name: 'David Kim',
    position: 'Export Sales Manager',
    company: 'GBCOSPACK',
    issuedDate: '2026-08-24',
  },
  statusBadge: { label: 'Issued', tone: 'success' },
};